import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Package, Scissors, Download, Filter, CheckCircle, XCircle, Trash2, Eye } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { format, addDays, isAfter, isBefore, parseISO } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ErrorModal from '../components/modals/ErrorModal';

// Define the unified order type
interface UnifiedOrder {
  id: string;
  type: 'production' | 'sample';
  name: string;
  client_id: string;
  client_name: string;
  approved_file: string;
  shipping_label?: string;
  delivery_date: string;
  status: 'pending' | 'in_progress' | 'finished' | 'shipped';
  is_printed: boolean;
  original_source: 'express' | 'express_samples';
}

const Pedidos: React.FC = () => {
  const navigate = useNavigate();
  const { userRole, isAdmin } = useAuth();
  const { 
    express, 
    expressSamples, 
    clients, 
    updateExpress, 
    updateExpressSample,
    setError: setGlobalError
  } = useAppStore();
  
  // Redirect if not authorized
  useEffect(() => {
    if (userRole && userRole !== 'Administrador' && userRole !== 'Depósito') {
      navigate('/');
    }
  }, [userRole, navigate]);

  // State for filters
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [printedFilter, setPrintedFilter] = useState<string>('all');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Combine express orders and samples into a unified list
  const unifiedOrders = useMemo(() => {
    const orders: UnifiedOrder[] = [];

    // Add express orders
    express.forEach(order => {
      const client = clients.find(c => c.id === order.client_id);
      orders.push({
        id: order.id,
        type: 'production',
        name: order.name,
        client_id: order.client_id,
        client_name: client?.name || 'Cliente desconocido',
        approved_file: order.approved_file,
        shipping_label: order.shipping_label,
        delivery_date: order.delivery_date,
        status: order.status,
        is_printed: order.is_printed || false,
        original_source: 'express'
      });
    });

    // Add express samples
    expressSamples.forEach(sample => {
      const client = clients.find(c => c.id === sample.client_id);
      orders.push({
        id: sample.id,
        type: 'sample',
        name: sample.name,
        client_id: sample.client_id,
        client_name: client?.name || 'Cliente desconocido',
        approved_file: sample.approved_file,
        shipping_label: sample.shipping_label,
        delivery_date: sample.delivery_date,
        status: sample.status,
        is_printed: sample.is_printed || false,
        original_source: 'express_samples'
      });
    });

    return orders;
  }, [express, expressSamples, clients]);

  // Apply filters to unified orders
  const filteredOrders = useMemo(() => {
    return unifiedOrders.filter(order => {
      // Filter by client
      if (clientFilter !== 'all' && order.client_id !== clientFilter) {
        return false;
      }

      // Filter by type (production/sample)
      if (typeFilter !== 'all' && order.type !== typeFilter) {
        return false;
      }

      // Filter by printed/plain
      if (printedFilter !== 'all') {
        const isPrinted = printedFilter === 'printed';
        if (order.is_printed !== isPrinted) {
          return false;
        }
      }

      // Filter by delivery date
      if (deliveryDateFilter !== 'all') {
        const today = new Date();
        const deliveryDate = parseISO(order.delivery_date);
        
        switch (deliveryDateFilter) {
          case '2days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 2));
          case '7days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 7));
          case '15days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 15));
          default:
            return true;
        }
      }

      return true;
    });
  }, [unifiedOrders, clientFilter, typeFilter, printedFilter, deliveryDateFilter]);

  // Group orders by status
  const ordersByStatus = useMemo(() => {
    return {
      pending: filteredOrders.filter(order => order.status === 'pending'),
      in_progress: filteredOrders.filter(order => order.status === 'in_progress'),
      finished: filteredOrders.filter(order => order.status === 'finished'),
      shipped: filteredOrders.filter(order => order.status === 'shipped'),
    };
  }, [filteredOrders]);

  // Handle drag and drop
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, order: UnifiedOrder) => {
    e.dataTransfer.setData('orderId', order.id);
    e.dataTransfer.setData('orderType', order.original_source);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, newStatus: 'pending' | 'in_progress' | 'finished' | 'shipped') => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData('orderId');
    const orderType = e.dataTransfer.getData('orderType');
    
    try {
      if (orderType === 'express') {
        await updateExpress(orderId, { status: newStatus });
      } else if (orderType === 'express_samples') {
        await updateExpressSample(orderId, { status: newStatus });
      }
    } catch (error: any) {
      console.error('Error updating order status:', error);
      setError(error.message || 'Error al actualizar el estado del pedido');
      setShowErrorModal(true);
    }
  };

  // View order details
  const handleViewOrder = (order: UnifiedOrder) => {
    if (order.original_source === 'express') {
      navigate('/express');
    } else {
      navigate('/express/muestras');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setClientFilter('all');
    setTypeFilter('all');
    setPrintedFilter('all');
    setDeliveryDateFilter('all');
  };

  // Render order card
  const renderOrder = (order: UnifiedOrder) => {
    return (
      <div
        key={`${order.original_source}-${order.id}`}
        draggable
        onDragStart={(e) => handleDragStart(e, order)}
        className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow relative group"
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 dark:text-white">{order.name}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{order.client_name}</p>
            
            <div className="flex flex-wrap gap-2 mt-2">
              {/* Type badge */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                order.type === 'production' 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' 
                  : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200'
              }`}>
                {order.type === 'production' ? 'Producción' : 'Muestra'}
              </span>
              
              {/* Printed/Plain badge */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                order.is_printed 
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' 
                  : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
              }`}>
                {order.is_printed ? 'Estampado' : 'Liso'}
              </span>
            </div>
            
            {/* Delivery date */}
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
              <Calendar className="h-4 w-4 mr-1" />
              {format(new Date(order.delivery_date), 'dd/MM/yyyy')}
            </div>
            
            {/* DTF Status */}
            {order.original_source === 'express' && (
              <>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span className="mr-1">DTF enviado:</span>
                  {express.find(e => e.id === order.id)?.dtf_printed === 'Sí' ? (
                    <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                </div>
                
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span className="mr-1">DTF retirado:</span>
                  {express.find(e => e.id === order.id)?.dtf_picked_up === 'Sí' ? (
                    <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                </div>
              </>
            )}
            
            {order.original_source === 'express_samples' && (
              <>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span className="mr-1">DTF enviado:</span>
                  {expressSamples.find(s => s.id === order.id)?.dtf_printed === 'Sí' ? (
                    <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                </div>
                
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span className="mr-1">DTF retirado:</span>
                  {expressSamples.find(s => s.id === order.id)?.dtf_picked_up === 'Sí' ? (
                    <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                </div>
              </>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            {/* Approved file */}
            {order.approved_file && (
              <a
                href={order.approved_file}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center"
              >
                <Eye className="h-3 w-3 mr-1" />
                Ficha
              </a>
            )}
            
            {/* Shipping label */}
            {order.shipping_label && (
              <a
                href={order.shipping_label}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center"
              >
                <Download className="h-3 w-3 mr-1" />
                Etiqueta
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Pedidos</h2>
        <Button
          variant="outline"
          icon={<Filter className="h-4 w-4" />}
          onClick={resetFilters}
        >
          Limpiar Filtros
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Select
                label="Cliente"
                value={clientFilter}
                onChange={setClientFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...clients.map(client => ({
                    value: client.id,
                    label: client.name
                  }))
                ]}
              />
            </div>
            
            <div className="w-40">
              <Select
                label="Tipo"
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'production', label: 'Producción' },
                  { value: 'sample', label: 'Muestra' }
                ]}
              />
            </div>
            
            <div className="w-40">
              <Select
                label="Estampado/Liso"
                value={printedFilter}
                onChange={setPrintedFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'printed', label: 'Estampado' },
                  { value: 'plain', label: 'Liso' }
                ]}
              />
            </div>
            
            <div className="w-40">
              <Select
                label="Entrega"
                value={deliveryDateFilter}
                onChange={setDeliveryDateFilter}
                options={[
                  { value: 'all', label: 'Todas' },
                  { value: '2days', label: 'Próx. 2 días' },
                  { value: '7days', label: 'Próx. 7 días' },
                  { value: '15days', label: 'Próx. 15 días' }
                ]}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Tablero de Pedidos" />
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Pending Column */}
            <div 
              className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'pending')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Pendiente <span className="font-normal text-sm">({ordersByStatus.pending.length})</span>
                </h3>
              </div>
              <div className="space-y-3">
                {ordersByStatus.pending.map(renderOrder)}
                {ordersByStatus.pending.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos pendientes
                  </div>
                )}
              </div>
            </div>

            {/* In Progress Column */}
            <div 
              className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'in_progress')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  En Proceso <span className="font-normal text-sm">({ordersByStatus.in_progress.length})</span>
                </h3>
              </div>
              <div className="space-y-3">
                {ordersByStatus.in_progress.map(renderOrder)}
                {ordersByStatus.in_progress.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos en proceso
                  </div>
                )}
              </div>
            </div>

            {/* Finished Column */}
            <div 
              className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'finished')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Terminado <span className="font-normal text-sm">({ordersByStatus.finished.length})</span>
                </h3>
              </div>
              <div className="space-y-3">
                {ordersByStatus.finished.map(renderOrder)}
                {ordersByStatus.finished.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos terminados
                  </div>
                )}
              </div>
            </div>

            {/* Shipped Column */}
            <div 
              className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'shipped')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Despachado <span className="font-normal text-sm">({ordersByStatus.shipped.length})</span>
                </h3>
              </div>
              <div className="space-y-3">
                {ordersByStatus.shipped.map(renderOrder)}
                {ordersByStatus.shipped.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos despachados
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </div>
  );
};

export default Pedidos;